import calc from "@/helper/calc";
import classes from "@/helper/classes";
import helper from "@/helper/helper";
import {
  getCartSellingPlanId,
  getInstallmentsCount,
  getVariantTitle,
  getVariantTitleAliases,
  getVariantType,
  toShopifyResourceId,
} from "@/helper/firmhouse";
import { nextTick } from "vue";
import { createStore } from "vuex";
const axiosInstance = require("./api_auth").default;
import axios from "axios";
import { getProductIdByLegacyId } from "@/helper/legacy_mapper";
import { loadSubdomainConfig } from "@/helper/subdomain_config";

const getDurationFromDays = (days) =>
  (days / 30) % 1 > 0.3 ? Math.ceil(days / 30) : Math.floor(days / 30);
const CART_ADD_MAX_ATTEMPTS = 10;
const CART_ADD_RETRY_DELAY_MS = 2000;
const DEBUG_PREVENT_SHOPIFY_REDIRECT = false;
const FIRMHOUSE_TEMPLATE_SUFFIX = "firmhouse-subscriptions";
const SHOPIFY_BASE_CURRENCY = "CHF";
const SHOPIFY_REORDER_DISCOUNT_FACTOR = 0.95;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const amountInGrams = (amount, unit) => {
  if (unit === "g") {
    return amount;
  }

  if (unit === "mg") {
    return amount / 1000;
  }

  if (unit === "mcg") {
    return amount / 1000000;
  }

  return amount;
};

const formatProductionSheetQuantity = (value) => Number(value).toFixed(3);

const buildProductionSheetItems = (order) => {
  const capsulas =
    order.fixedCapsulasPerDay ||
    order.totalCapsulas ||
    calc.totalCapsulas(order.personalMix);
  const totalCapsulasInMix = 300;
  const capsulaVolume = 0.68;
  const densityMaltodextrin = 0.79;
  let cumulatedVolume = 0;

  const items = order.personalMix.reduce((productionItems, product) => {
    const amount = Number(product.amount || 0);
    const concentration = Number(product.concentration || 0);
    const density = Number(product.density || 0);

    if (concentration <= 0 || capsulas <= 0) {
      return productionItems;
    }

    const quantity =
      ((amountInGrams(amount, product.unit) / concentration) *
        totalCapsulasInMix) /
      capsulas;

    if (density > 0) {
      cumulatedVolume += quantity / density;
    }

    productionItems.push({
      article_id: product.platform_id || product.id,
      name: `${
        product.ingredientNameOriginal || product.ingredientName || product.name
      } (${product.name})`,
      quantity: formatProductionSheetQuantity(quantity),
      unit: "g",
    });

    return productionItems;
  }, []);

  const maltodextrinQuantity =
    (totalCapsulasInMix * capsulaVolume - cumulatedVolume) *
    densityMaltodextrin;
  items.push({
    article_id: null,
    name: "Maltodextrin",
    quantity: formatProductionSheetQuantity(Math.max(maltodextrinQuantity, 0)),
    unit: "g",
  });

  return items;
};

const applySubdomainConfigToOrder = (order, subdomainConfig) => {
  if (!order || !subdomainConfig) {
    return;
  }

  order.subdomainConfig = subdomainConfig;
  order.fixedCapsulasPerDay = subdomainConfig.fixedCapsulasPerDay || null;
  order.intakeRecommendation = subdomainConfig.intakeRecommendation || null;
  order.intakeRecommendationMin =
    subdomainConfig.intakeRecommendationMin || null;
  order.intakeRecommendationMax =
    subdomainConfig.intakeRecommendationMax || null;
  order.productionSheetCapsulas =
    subdomainConfig.productionSheetCapsulas ||
    subdomainConfig.intakeRecommendation ||
    null;
  order.productionSheetCapsulasMin =
    subdomainConfig.productionSheetCapsulasMin ||
    subdomainConfig.intakeRecommendationMin ||
    null;
  order.productionSheetCapsulasMax =
    subdomainConfig.productionSheetCapsulasMax ||
    subdomainConfig.intakeRecommendationMax ||
    null;

  if (subdomainConfig.capsuleColor) {
    order.capsuleColor = subdomainConfig.capsuleColor;
  }

  if (subdomainConfig.capsuleColorHex) {
    order.capsuleColorHex = subdomainConfig.capsuleColorHex;
  }

  if (subdomainConfig.capsulaMaterial) {
    order.capsulaMaterial = subdomainConfig.capsulaMaterial;
  }

  if (subdomainConfig.fragrance) {
    order.fragrance = subdomainConfig.fragrance;
  }

  if (subdomainConfig.labelPattern) {
    order.labelPattern = subdomainConfig.labelPattern;
  }

  if (order.fixedCapsulasPerDay) {
    order.totalCapsulas = order.fixedCapsulasPerDay;
  }
};

const updateRobotsMeta = (content) => {
  if (typeof document === "undefined") {
    return;
  }

  let robotsMeta = document.querySelector('meta[name="robots"]');

  if (!content) {
    if (robotsMeta?.parentNode) {
      robotsMeta.parentNode.removeChild(robotsMeta);
    }

    return;
  }

  if (!robotsMeta) {
    robotsMeta = document.createElement("meta");
    robotsMeta.setAttribute("name", "robots");
    document.head.appendChild(robotsMeta);
  }

  robotsMeta.setAttribute("content", content);
};

const shouldNoindexSubdomain = (subdomainConfig) =>
  Boolean(
    subdomainConfig?.noIndex ||
      String(subdomainConfig?.labelPattern || "").toLowerCase() === "care" ||
      String(subdomainConfig?.name || "").toLowerCase() === "care"
  );

const orderCapsulas = (order) =>
  order.fixedCapsulasPerDay ||
  order.totalCapsulas ||
  calc.totalCapsulas(order.personalMix);

const fixedPresentmentPriceForOrder = (
  order,
  currency,
  subscriptionOptions
) => {
  if (currency === SHOPIFY_BASE_CURRENCY) {
    return null;
  }

  let amount = calc.orderPrice(order.personalMix, currency, subscriptionOptions);

  if (subscriptionOptions?.monthly) {
    const days = Math.round(calc.capsulasPerMixReal() / orderCapsulas(order));
    amount = amount / getInstallmentsCount(days);
  }

  if (subscriptionOptions?.reorder) {
    amount = amount / SHOPIFY_REORDER_DISCOUNT_FACTOR;
  }

  return {
    amount: amount.toFixed(2),
    currency_code: currency,
  };
};

const sanitizeCartItem = (item) => {
  const cartItem = {
    id: item.id,
    quantity: item.quantity,
  };

  if (item.selling_plan) {
    cartItem.selling_plan = item.selling_plan;
  }

  return cartItem;
};

const shouldRetryShopifyCartAdd = (errorText, lineItems) => {
  const normalizedError = errorText || "";
  const hasSellingPlan = lineItems.some((item) => item.selling_plan);
  const sellingPlanNotReady =
    hasSellingPlan &&
    /selling plan/i.test(normalizedError) &&
    /(not available|available yet|invalid)/i.test(normalizedError);
  const variantNotReady =
    /(could not find variant|can(?:not|'t) find variant|kann variante nicht finden)/i.test(
      normalizedError
    );

  return sellingPlanNotReady || variantNotReady;
};

const isRetryableCartAddStatus = (status) =>
  [404, 409, 422, 429].includes(status);
const isNetworkCartAddError = (error) =>
  error instanceof TypeError || /failed to fetch/i.test(error?.message || "");

const PRODUCT_STATUS_POLL_INTERVAL_MS = 2000;
const PRODUCT_STATUS_MAX_WAIT_MS = 120000;
const CHECKOUT_EVENT_ENDPOINT = "/public/items/checkout_event";

// Meldet Checkout-Ereignisse an die API, damit wir Fehlschlaege nachvollziehen
// koennen, ohne darauf angewiesen zu sein, dass jemand im richtigen Moment die
// Entwicklertools offen hat.
//
// Bewusst ohne await und komplett gekapselt: Dieser Aufruf darf unter keinen
// Umstaenden einen Kauf verhindern oder verzoegern.
const logCheckoutEvent = (payload) => {
  try {
    axiosInstance
      .post(CHECKOUT_EVENT_ENDPOINT, {
        ...payload,
        subdomain: window.location.hostname,
        config_url: window.location.href,
        user_agent: navigator.userAgent,
        occurred_at: new Date().toISOString(),
      })
      .catch((error) => console.warn("checkout event not logged", error));
  } catch (error) {
    console.warn("checkout event not logged", error);
  }
};

// Ordnet einen Fehler dem Schritt zu, an dem er aufgetreten ist -- das ist die
// Information, die uns bei jedem bisherigen Vorfall gefehlt hat.
const checkoutFailureStep = (error) => {
  if (error?.response?.status) return "create_product_api";
  if (/verification failed|Timed out waiting/i.test(error?.message || "")) {
    return "verification";
  }
  if (/Missing .* Shopify variant/i.test(error?.message || "")) {
    return "variant_lookup";
  }
  if (error?.request) return "network";

  return "create_product";
};

const fetchProductStatus = async (jobStatusId) => {
  const { data } = await axiosInstance.post("/public/items/product_status", {
    job_status_id: jobStatusId,
  });

  return data;
};

// Wartet darauf, dass der Sidekiq-Job die Firmhouse-Verifikation abgeschlossen
// hat. Frueher steckte dieses Warten in create_product: ein Checkout belegte
// einen von drei Puma-Slots fuer bis zu 30 Sekunden und lief dabei gegen das
// harte Router-Timeout. Jede Abfrage hier ist eine DB-Abfrage von
// Millisekunden, deshalb duerfen wir so lange warten, wie Firmhouse braucht.
const waitForProductReady = async (jobStatusIds) => {
  const deadline = Date.now() + PRODUCT_STATUS_MAX_WAIT_MS;
  const pending = new Set(jobStatusIds.filter(Boolean));
  const problems = [];

  while (pending.size && Date.now() < deadline) {
    for (const jobStatusId of Array.from(pending)) {
      let status;

      try {
        status = await fetchProductStatus(jobStatusId);
      } catch (error) {
        // Transienter Fehler: naechste Runde erneut versuchen, statt den
        // Checkout wegen eines einzelnen Netzwerkschluckaufs abzubrechen.
        console.warn("product_status poll failed", error);
        continue;
      }

      if (status?.ready) {
        pending.delete(jobStatusId);
        continue;
      }

      if (status?.finished) {
        pending.delete(jobStatusId);
        problems.push(status?.error || "Shopify product verification failed");
      }
    }

    if (!pending.size) break;

    await wait(PRODUCT_STATUS_POLL_INTERVAL_MS);
  }

  if (pending.size) {
    problems.push("Timed out waiting for Shopify product verification");
  }

  return problems;
};

const buildCartFormInput = (documentRef, name, value) => {
  const input = documentRef.createElement("input");
  input.type = "hidden";
  input.name = name;
  input.value = value;
  return input;
};

const submitCheckoutForm = ({ action, checkoutPath, items }) => {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  form.target = "_top";
  form.style.display = "none";

  items.forEach((item, index) => {
    form.appendChild(
      buildCartFormInput(document, `items[${index}][id]`, item.id)
    );
    form.appendChild(
      buildCartFormInput(document, `items[${index}][quantity]`, item.quantity)
    );

    if (item.selling_plan) {
      form.appendChild(
        buildCartFormInput(
          document,
          `items[${index}][selling_plan]`,
          item.selling_plan
        )
      );
    }
  });

  form.appendChild(buildCartFormInput(document, "return_to", checkoutPath));
  document.body.appendChild(form);
  form.submit();
};

const getShopifyDescriptionCopy = (language) => {
  const copy = {
    de: {
      label: "Label",
      list: "Liste",
      ingredients: "Zutatenliste",
      language: "Sprache",
    },
    fr: {
      label: "Étiquette",
      list: "Liste",
      ingredients: "Liste des ingrédients",
      language: "Langue",
    },
    en: {
      label: "Label",
      list: "List",
      ingredients: "Ingredients list",
      language: "Language",
    },
    it: {
      label: "Etichetta",
      list: "Lista",
      ingredients: "Elenco ingredienti",
      language: "Lingua",
    },
    cs: {
      label: "Štítek",
      list: "Seznam",
      ingredients: "Seznam složek",
      language: "Jazyk",
    },
  };

  return copy[language] || copy.en;
};

const buildLocalizedShopifyDescription = ({
  order,
  language,
  quizInfoHtml,
}) => {
  const copy = getShopifyDescriptionCopy(language);
  const labelUrl = order.dynamicPdfLabelURL || order.pdfLabelURL;
  const sections = [
    `${copy.label}: <a href='${labelUrl}' target='blank'>${copy.label}</a>`,
    `${copy.list}: <a href='${order.pdfListURL}' target='blank'>${copy.list}</a>`,
    `<hr/>${order.productString.replace(/\n/g, "<br/>")}`,
  ];

  if (order.ingredientsOverview) {
    sections.push(
      `<hr/><strong>${copy.ingredients}</strong><br/>${order.ingredientsOverview}`
    );
  }

  if (quizInfoHtml) {
    sections.push(`<hr/>${quizInfoHtml}`);
  }

  sections.push(`<hr/>${copy.language}: ${language}`);

  return `${sections.join("<br/><br/>")}<br/><br/>`;
};

// Bitly API service for URL shortening
const shortenUrlWithBitly = async (longUrl) => {
  try {
    // Trim any whitespace or special characters from the token
    const token = process.env.VUE_APP_BITLY_TOKEN?.trim();

    if (!token) {
      throw new Error("Bitly token is not configured");
    }

    console.log("Attempting to shorten URL with Bitly:", longUrl);

    const response = await fetch("https://api-ssl.bitly.com/v4/shorten", {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        long_url: longUrl,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Bitly API error response:", errorText);
      throw new Error(
        `Bitly API error: ${response.status} ${response.statusText} - ${errorText}`
      );
    }

    const data = await response.json();
    console.log("Successfully shortened URL:", data.link);
    return data.link;
  } catch (error) {
    console.error("Failed to shorten URL with Bitly:", error);
    throw error;
  }
};

// Create a new store instance.
const store = createStore({
  state() {
    let storeObject = {
      currentOrder: {},
      language: "de",
      currency: "CHF",
      monthly: true, // installments
      reorder: true, // subscription / automatic reorder
      isBloodTestChecked: false,
      branding: {},
      subdomainConfig: null,
      subdomainConfigLoading: false,
      orders: [],
      upsellingOrders: [],
      products: [],
      productsResponse: [],
      productUpselling: [],
      showUpsellings: [],
      scene: "intro",
      sceneSteps: [
        "intro",
        "name",
        "hello",
        "gender",
        "ageRange",
        "email",
        "weight",
        "weightHappyness",
        "height",
        "pregnant",
        "healthGoals",
        "healthPrio",
        "additionalQuestion",
        "diet",
        "allergic",
        "intolerances",
        "thanks",
        "fishIntake",
        "veggyIntake",
        "exercise",
        "alcohol",
        "smoke",
        "bloodPressureDrugs",
        "calculation",
        "personalize",
        "details",
        "checkout-waiting",
      ],
      sceneStepsType: "default",
      filteredSceneSteps: [],
      isExpertMode: false,
      hashRead: false,
      hash: null,
      error: false,
      errorMessage: null,
      apiErrorCounter: 0,
      debugMode: false,
      loading: false,
    };

    // init new order
    storeObject.currentOrder = new classes.Order(1);
    storeObject.orders[0] = storeObject.currentOrder;
    return storeObject;
  },
  getters: {
    scene(state) {
      return state.scene;
    },
    loading(state) {
      return state.loading;
    },
    sceneSteps(state) {
      return state.sceneSteps;
    },
    sceneStepsType(state) {
      return state.sceneStepsType;
    },
    language(state) {
      return state.language;
    },
    currency(state) {
      return state.currency;
    },
    currencySymbol(state) {
      if (state.currency == "CHF") {
        return "CHF";
      } else if (state.currency == "EUR") {
        return "€";
      } else if (state.currency == "USD") {
        return "$";
      }
    },
    monthly(state) {
      return state.monthly;
    },
    reorder(state) {
      return state.reorder;
    },
    isBloodTestChecked(state) {
      return state.isBloodTestChecked;
    },
    subscriptionOptions(state) {
      return {
        monthly: state.monthly,
        reorder: state.reorder,
        isBloodTestChecked: state.isBloodTestChecked,
      };
    },
    debugMode(state) {
      return state.debugMode;
    },
    logo(state) {
      return state.branding.logo;
    },
    logoBlack(state) {
      return state.branding.logoBlack;
    },
    branding(state) {
      return state.branding;
    },
    brandingName(state) {
      return state.branding.name;
    },
    subdomainConfig(state) {
      return state.subdomainConfig;
    },
    subdomainConfigLoading(state) {
      return state.subdomainConfigLoading;
    },
    showIndyVitLogo(state) {
      return state.branding.showIndyVitLogo;
    },
    backButtonUrl(state) {
      return state.branding.backButtonUrl;
    },
    disableCheckout(state) {
      return state.branding.disableCheckout;
    },
    error(state) {
      return state.error;
    },
    errorMessage(state) {
      return state.errorMessage;
    },
    apiErrorCounter(state) {
      return state.apiErrorCounter;
    },
    currentOrder(state) {
      return state.currentOrder;
    },
    orders(state) {
      return state.orders;
    },
    upsellingOrders(state) {
      return state.upsellingOrders;
    },
    orderId(state) {
      return state.currentOrder.id;
    },
    name(state) {
      return state.currentOrder.name;
    },
    id(state) {
      return state.currentOrder.id;
    },
    label(state) {
      return state.currentOrder.label;
    },
    consent(state) {
      return state.currentOrder.consent;
    },
    gender(state) {
      return state.currentOrder.gender;
    },
    ageRange(state) {
      return state.currentOrder.ageRange;
    },
    email(state) {
      return state.currentOrder.email;
    },
    newsletter(state) {
      return state.currentOrder.newsletter;
    },
    weight(state) {
      return state.currentOrder.weight;
    },
    weightHappyness(state) {
      return state.currentOrder.weightHappyness;
    },
    height(state) {
      return state.currentOrder.height;
    },
    pregnant(state) {
      return state.currentOrder.pregnant;
    },
    healthGoals(state) {
      return state.currentOrder.healthGoals;
    },
    healthPossibleOptions(state) {
      return state.currentOrder.healthPossibleOptions;
    },
    healthPrio(state) {
      return state.currentOrder.healthPrio;
    },
    diet(state) {
      return state.currentOrder.diet;
    },
    isVeggy(state) {
      if (
        state.currentOrder.diet == "vegan" ||
        state.currentOrder.diet == "vegetarisch"
      ) {
        return true;
      }
      return false;
    },
    isAlcohol(state) {
      if (state.currentOrder.alcohol != "none") {
        return true;
      }
      return false;
    },
    isPregnant(state) {
      if (state.currentOrder.pregnant == "yes") {
        return true;
      }
      return false;
    },
    isSmoker(state) {
      if (state.currentOrder.smoke == "yes") {
        return true;
      }
      return false;
    },
    allergic(state) {
      return state.currentOrder.allergic;
    },
    intolerances(state) {
      return state.currentOrder.intolerances;
    },
    veggyIntake(state) {
      return state.currentOrder.veggyIntake;
    },
    fishIntake(state) {
      return state.currentOrder.fishIntake;
    },
    exercise(state) {
      return state.currentOrder.exercise;
    },
    alcohol(state) {
      return state.currentOrder.alcohol;
    },
    smoke(state) {
      return state.currentOrder.smoke;
    },
    bloodPressureDrugs(state) {
      return state.currentOrder.bloodPressureDrugs;
    },
    factors(state) {
      return state.currentOrder.factors;
    },
    labelColor(state) {
      return state.currentOrder.labelColor;
    },
    labelPattern(state) {
      return state.currentOrder.labelPattern;
    },
    labelColorHexTop(state) {
      return state.currentOrder.labelColorHexTop;
    },
    labelColorHexBottom(state) {
      return state.currentOrder.labelColorHexBottom;
    },
    capsuleColor(state) {
      return state.currentOrder.capsuleColor;
    },
    capsuleColorHex(state) {
      return state.currentOrder.capsuleColorHex;
    },
    capsulaMaterial(state) {
      return state.currentOrder.capsulaMaterial;
    },
    fragrance(state) {
      return state.currentOrder.fragrance;
    },
    isCalculated(state) {
      return state.currentOrder.isCalculated;
    },
    personalMix(state) {
      return state.currentOrder.personalMix;
    },
    productString(state) {
      return state.currentOrder.productString;
    },
    ingredientsOverview(state) {
      return state.currentOrder.ingredientsOverview;
    },
    products(state) {
      return state.products;
    },
    productUpselling(state) {
      return state.productUpselling;
    },
    showUpsellings(state) {
      return state.showUpsellings;
    },
    totalCapsulas(state) {
      return orderCapsulas(state.currentOrder);
    },
    lastXDays(state) {
      return Math.round(
        calc.capsulasPerMixReal() / orderCapsulas(state.currentOrder)
      );
    },
    capsulaFill(state) {
      return state.currentOrder.capsulaFill;
    },
    labelStep(state) {
      return state.currentOrder.labelStep;
    },
    additionalQuestion(state) {
      return state.currentOrder.additionalQuestion;
    },
    isExpertMode(state) {
      return state.isExpertMode;
    },
    hashRead(state) {
      return state.hashRead;
    },
    hash(state) {
      return state.hash;
    },
  },
  actions: {
    async getSubdomainConfig(state) {
      try {
        const subdomainConfig = await loadSubdomainConfig(
          axiosInstance,
          window.location.hostname
        );

        if (subdomainConfig) {
          state.commit("setSubdomainConfig", subdomainConfig);

          if (
            subdomainConfig.subdomainExpertMode &&
            window.location.hostname.indexOf(
              subdomainConfig.subdomainExpertMode
            ) === 0
          ) {
            state.commit("showExpertMode");
          }
        }
      } catch (error) {
        console.warn("Failed to load subdomain config from PIM", error);
      } finally {
        state.commit("finishSubdomainConfigLoading");
      }
    },
    getUpsellingProducts(state) {
      axiosInstance
        .get("/public/items/upselling?per_page=100")
        .then((response) => {
          state.commit("saveUpselling", response.data);
        })
        .catch((error) => {
          // retry after 1 seconds
          setTimeout(() => {
            state.commit("saveUpselling");
          }, 1000);
          console.log(error);
        });
    },
    getProducts(state) {
      state.commit("isCalculated", true);
      axiosInstance
        .get("/public/items/products?per_page=100")
        .then((response) => {
          state.commit("saveProducts", response.data);
          state.commit("calculateProductResponse");
          state.commit("readSavedHash");
        })
        .catch((error) => {
          // retry after 5 seconds
          setTimeout(() => {
            state.dispatch("getProducts");
          }, 5 * 1000);
          console.log(error);
        });
    },
    async addToCart(state, payload) {
      const ctx = payload.ctx;

      this.commit("setLoading", true);

      let reqPromise = [];
      for (let order of state.getters.orders) {
        reqPromise.push(
          order.generatePDF(
            ctx,
            state.getters.branding,
            state.getters.language,
            state.getters.currency
          )
        );
      }
      Promise.all(reqPromise)
        .then((results) => {
          for (let result of results) {
            if (!result) {
              logCheckoutEvent({
                event: "checkout_failed",
                step: "pdf",
                message: "Label/PDF generation returned no result",
              });
              this.commit("apiError");
              return;
            }
          }
          this.dispatch("createProduct");
        })
        .catch((error) => {
          logCheckoutEvent({
            event: "checkout_failed",
            step: "pdf",
            message: error?.message || "Label/PDF generation failed",
          });
          this.commit("apiError");
        });
    },
    async createProduct(state) {
      const email = state.getters?.email;
      const gender = state.getters?.gender;
      const language = state.getters?.language;
      const abFlow = state.getters?.sceneStepsType;
      const originalConfigUrl = window.location.href;
      const orders = state.getters.orders;
      let config_url = originalConfigUrl;

      try {
        config_url = await shortenUrlWithBitly(originalConfigUrl);
      } catch (error) {
        console.warn(
          "Failed to shorten config URL with Bitly, using original URL:",
          error
        );
        config_url = originalConfigUrl;
      }

      try {
        const responses = await Promise.all(
          orders.map((order) => {
            const days = Math.round(
              calc.capsulasPerMixReal() / orderCapsulas(order)
            );
            const duration = getDurationFromDays(days);

            console.log("PDF-Label", order.pdfLabelURL);

            const installmentCount = getInstallmentsCount(days);
            return axiosInstance.post("/public/items/create_product", {
              title: order.name + " Indymix",
              html_description: buildLocalizedShopifyDescription({
                order,
                language: state.getters.language,
                quizInfoHtml: helper.getQuizInfos(order),
              }),
              product_image_url:
                order.productImageUrl ||
                "https://pocket-rocket-customers.s3.eu-central-1.amazonaws.com/indyvit/indyvit-asset.png",
              tags: `purchase,${state.getters.brandingName.replace(
                " ",
                "_"
              )},${orderCapsulas(
                order
              )}_capsuleDay,${installmentCount}_installments,${order.healthGoals.join(
                ","
              )}`,
              products: order.personalMix.map((product) => {
                return {
                  item_id: product.id,
                  quantity: product.amountMg,
                };
              }),
              ingredients_overview: order.ingredientsOverview,
              production_sheet_items: buildProductionSheetItems(order),
              label_config: order.labelConfig,
              subdomain_config: order.subdomainConfig,
              label_url: order.pdfLabelURL,
              dynamic_label_url: order.dynamicPdfLabelURL,
              package_list_url: order.pdfListURL,
              email: email,
              gender: gender,
              language: language,
              config_url: config_url,
              ab_flow: abFlow,
              discount_percentage: this.getters.reorder ? 5 : 0,
              subscription: this.getters.monthly,
              subscription_duration: duration,
              subscription_interval: !this.getters.reorder
                ? 0
                : this.getters.monthly
                ? 1
                : duration,
              interval_days: days,
              fixed_capsulas_per_day: order.fixedCapsulasPerDay || null,
              // Das Gem wartet dann nicht mehr im Request auf Firmhouse,
              // sondern stellt einen Job ein und liefert eine job_status_id.
              defer_verification: true,
              template_suffix: FIRMHOUSE_TEMPLATE_SUFFIX,
              fixed_presentment_price: fixedPresentmentPriceForOrder(
                order,
                this.getters.currency,
                this.getters.subscriptionOptions
              ),
              currency_multiplier: calc.priceLevelCurrencyMultiplicator(
                this.getters.currency
              ),
            });
          })
        );

        const variantType = getVariantType({
          monthly: this.getters.monthly,
          reorder: this.getters.reorder,
        });
        const desiredVariantTitle = getVariantTitle(variantType);
        const desiredVariantTitles = getVariantTitleAliases(variantType);

        const productTargets = responses.map((response, index) => {
          const order = orders[index];
          const days = Math.round(
            calc.capsulasPerMixReal() / orderCapsulas(order)
          );
          // Ohne job_status_id laeuft noch das alte Gem, das synchron geprueft
          // hat -- dann gelten weiterhin die Flags aus der Antwort. Damit
          // laesst sich das Gem unabhaengig vom Frontend ausrollen.
          if (!response?.data?.job_status_id) {
            if (response?.data?.selling_plan_group_verified === false) {
              throw new Error(
                `Firmhouse selling plan group verification failed for ${days} days.`
              );
            }

            if (response?.data?.storefront_variant_verified === false) {
              throw new Error(
                `Shopify storefront variant verification failed for ${days} days.`
              );
            }
          }

          const variants =
            response?.data?.results_variant_creation?.data
              ?.productVariantsBulkCreate?.productVariants || [];
          const relevantVariant = variants.find((variant) => {
            const variantOptions = (variant?.selectedOptions || [])
              .map((option) => option?.value)
              .filter(Boolean);

            return variantOptions.some((option) =>
              desiredVariantTitles.includes(option)
            );
          });

          if (!relevantVariant) {
            throw new Error(
              `Missing ${desiredVariantTitle} Shopify variant for ${days} days. Checked aliases: ${desiredVariantTitles.join(
                ", "
              )}.`
            );
          }

          return {
            id: toShopifyResourceId(relevantVariant.id),
            quantity: 1,
            selling_plan: toShopifyResourceId(
              getCartSellingPlanId({
                days,
                monthly: this.getters.monthly,
                reorder: this.getters.reorder,
              })
            ),
          };
        });

        const jobStatusIds = responses
          .map((response) => response?.data?.job_status_id)
          .filter(Boolean);

        const productIds = responses
          .map(
            (response) =>
              response?.data?.results_product_create?.data?.productCreate
                ?.product?.id
          )
          .filter(Boolean);

        if (jobStatusIds.length) {
          const problems = await waitForProductReady(jobStatusIds);

          if (problems.length) {
            const notReady = new Error(problems.join("; "));
            notReady.jobStatusIds = jobStatusIds;
            notReady.productIds = productIds;
            throw notReady;
          }
        }

        state.dispatch("createCheckout", { items: productTargets });
      } catch (error) {
        console.error("Failed to create Firmhouse product", error);
        logCheckoutEvent({
          event: "checkout_failed",
          step: checkoutFailureStep(error),
          message: error?.message,
          http_status: error?.response?.status,
          job_status_ids: error?.jobStatusIds,
          product_ids: error?.productIds,
          language: state.getters.language,
          currency: state.getters.currency,
          monthly: this.getters.monthly,
          reorder: this.getters.reorder,
        });
        this.commit("setLoading", false);
        this.commit("apiError", error?.message);
      }
    },
    async createCheckout(state, params) {
      const { items } = params;
      const lineItems = items.map(sanitizeCartItem);

      if (state.getters.isBloodTestChecked) {
        lineItems.push({
          id: 55094601351542,
          quantity: 1,
        });
      }

      const shopifyPathLanguages = ["fr", "en", "it", "cs"];
      const language = shopifyPathLanguages.includes(state.getters.language)
        ? state.getters.language
        : "";
      const shopifyUrl = "https://indyvit.com";
      // const shopifyUrl = "https://3ocjbj97lvlorexj-63265079543.shopifypreview.com";
      // const shopifyUrl = "https://2bjr90z75e6mlqet-63265079543.shopifypreview.com";
      // const shopifyUrl = "https://uvqiw7mgq3omxeln-63265079543.shopifypreview.com";
      const shopifyOrigin = new URL(shopifyUrl).origin;
      const isCrossOriginCartRequest = window.location.origin !== shopifyOrigin;
      const languagePath = language ? `/${language}` : "";
      const currencyQuery = `currency=${state.getters.currency}`;
      const cartAddUrl = `${shopifyUrl}${languagePath}/cart/add.js?${currencyQuery}`;
      const cartFormUrl = `${shopifyUrl}${languagePath}/cart/add?${currencyQuery}`;
      const cartPath = `${languagePath}/cart?${currencyQuery}`;
      const cartUrl = `${shopifyUrl}${cartPath}`;

      if (DEBUG_PREVENT_SHOPIFY_REDIRECT) {
        console.log("Debug Shopify cart payload", lineItems);
        console.log("Debug Shopify cart targets", {
          cartAddUrl,
          cartFormUrl,
          cartPath,
          cartUrl,
          isCrossOriginCartRequest,
        });
        this.commit("setLoading", false);
        return;
      }

      // Der Verlaufsmarker, auf den es ankommt: ab hier verlassen wir unseren
      // eigenen Code. Existiert dieses Ereignis, aber keine Bestellung, ist der
      // Kauf zwischen unserem Absenden und Shopify/Firmhouse gescheitert --
      // genau die Frage, die wir bei jedem bisherigen Vorfall nicht beantworten
      // konnten.
      logCheckoutEvent({
        event: "cart_submit",
        step: isCrossOriginCartRequest ? "cart_form_post" : "cart_add_fetch",
        cart_items: lineItems,
        language: state.getters.language,
        currency: state.getters.currency,
        monthly: state.getters.monthly,
        reorder: state.getters.reorder,
      });

      if (isCrossOriginCartRequest) {
        submitCheckoutForm({
          action: cartFormUrl,
          checkoutPath: cartPath,
          items: lineItems,
        });
        return;
      }

      try {
        let response;

        for (let attempt = 1; attempt <= CART_ADD_MAX_ATTEMPTS; attempt++) {
          try {
            response = await fetch(cartAddUrl, {
              method: "POST",
              credentials: "include",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({ items: lineItems }),
            });
          } catch (networkError) {
            if (attempt < CART_ADD_MAX_ATTEMPTS) {
              await wait(CART_ADD_RETRY_DELAY_MS);
              continue;
            }

            throw networkError;
          }

          if (response.ok) {
            break;
          }

          const errorText = await response.text();
          const shouldRetry =
            attempt < CART_ADD_MAX_ATTEMPTS &&
            (shouldRetryShopifyCartAdd(errorText, lineItems) ||
              isRetryableCartAddStatus(response.status));

          if (shouldRetry) {
            await wait(CART_ADD_RETRY_DELAY_MS);
            continue;
          }

          const cartError = new Error(
            `Shopify cart add failed: ${response.status} ${errorText}`
          );
          cartError.skipFormFallback = true;
          throw cartError;
        }

        window.parent.location = cartUrl;
      } catch (error) {
        const canFallback =
          !lineItems.some((item) => item.selling_plan) ||
          isNetworkCartAddError(error);

        logCheckoutEvent({
          event: "checkout_failed",
          step: "cart_add",
          message: error?.message,
          cart_items: lineItems,
          language: state.getters.language,
          currency: state.getters.currency,
        });

        if (error?.skipFormFallback) {
          console.error(error);
          this.commit("setLoading", false);
          this.commit("apiError", error?.message);
          return;
        }

        if (!canFallback) {
          console.error(
            "Skipping form fallback for selling-plan checkout",
            error
          );
          this.commit("setLoading", false);
          this.commit("apiError", error?.message || "Shopify cart add failed");
          return;
        }

        console.warn("Falling back to form-based Shopify cart add", error);
        submitCheckoutForm({
          action: cartFormUrl,
          checkoutPath: cartPath,
          items: lineItems,
        });
      }
    },
    async subscribeMail(state, kmailListID = "RFhypf") {
      this.commit("createHash");
      await nextTick();
      // if the current url already has a hash: Replace it with the new one
      const config_url =
        window.location.href.replace(/#.*$/, "") + state.getters.hash;
      const email = state.getters.email;
      const gender = state.getters.gender;
      const language = state.getters.language;
      const abFlow = state.getters.sceneStepsType;
      const originalNames = state.getters.orders
        ?.map((order) => order?.originalName || "")
        .join(", ");
      const mixNames = state.getters.orders
        ?.map((order) => order?.name || "")
        .join(", ");

      if (state.getters.sceneStepsType === "alteredOrder" || email.length > 3) {
        // https://manage.kmail-lists.com/ajax/subscriptions/subscribe
        let newsletterRequest = axios.create({
          baseURL: `https://manage.kmail-lists.com`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
            "cache-control": "no-cache",
          },
        });
        newsletterRequest
          .post(
            "/ajax/subscriptions/subscribe",
            "g=" +
              kmailListID +
              "&email=" +
              email +
              "&$fields=" +
              "gender, config_url, language, ab_flow, mix_names, original_names" +
              "&gender=" +
              gender +
              "&config_url=" +
              config_url +
              "&language=" +
              language +
              "&ab_flow=" +
              abFlow +
              "&mix_names=" +
              mixNames +
              "&original_names=" +
              originalNames
          )
          .then((response) => {
            console.log(response);
          })
          .catch((error) => {
            console.log(error);
          });
      }
    },
  },
  mutations: {
    setLanguage(state, val) {
      console.log("Language set to " + val);
      state.language = val;
    },
    setCurrency(state, val) {
      console.log("Currency set to " + val);
      state.currency = val;
    },
    setMonthly(state, val) {
      console.log("monthly set to " + val);
      state.monthly = val;

      this.commit("reCalculateOrders");
    },
    setReorder(state, val) {
      console.log("reorder set to " + val);
      state.reorder = val;

      this.commit("reCalculateOrders");
    },
    setBloodTestChecked(state, val) {
      console.log("isBloodTestChecked set to " + val);
      state.isBloodTestChecked = val;

      this.commit("reCalculateOrders");
    },
    setDebugMode(state) {
      state.debugMode = true;
    },
    setAltSceneOrder(state) {
      state.sceneSteps = [
        "intro",
        "name",
        "hello",
        "gender",
        "ageRange",
        "weight",
        "height",
        "pregnant",
        "diet",
        "allergic",
        "intolerances",
        "thanks",
        "fishIntake",
        "veggyIntake",
        "exercise",
        "alcohol",
        "smoke",
        "bloodPressureDrugs",
        "healthGoals",
        "healthPrio",
        "additionalQuestion",
        "calculation",
        "personalize",
        "details",
        "checkout-waiting",
      ];
      state.sceneStepsType = "alteredOrder";
    },
    setLabelColor(state, labelList) {
      state.currentOrder["labelColorHexBottom"] = labelList[1];
      state.currentOrder["labelColorHexTop"] = labelList[2];
      state.currentOrder["labelColor"] = labelList[0];
      console.log("Label color set", labelList);
    },
    setLabelPattern(state, pattern) {
      state.currentOrder["labelPattern"] = pattern;
      console.log("Label pattern set", pattern);
    },
    apiError(state, message = "API Error") {
      state.error = true;
      state.errorMessage = message || "API Error";
      state.apiErrorCounter++;
    },

    setLoading(state, val) {
      state.loading = val;
    },
    firstStep(state) {
      if (state.isExpertMode) {
        state.scene = "expert-mode";
      } else {
        state.scene = state.sceneSteps[0];
      }
    },
    nextStep(state) {
      // search for next scene in list to display
      let currentSceneIndex = 0;
      for (let index = 0; index < state.filteredSceneSteps.length; index++) {
        let sceneStep = state.filteredSceneSteps[index];
        if (sceneStep === state.scene) {
          currentSceneIndex = index;
          break;
        }
      }
      // change the scene
      state.scene = state.filteredSceneSteps[currentSceneIndex + 1];
    },
    prevStep(state) {
      // search for next scene in list to display
      let currentSceneIndex = 0;
      for (let index = 0; index < state.filteredSceneSteps.length; index++) {
        let sceneStep = state.filteredSceneSteps[index];
        if (
          sceneStep === state.scene &&
          index < state.filteredSceneSteps.length
        ) {
          currentSceneIndex = index;
          break;
        }
      }
      // change the scene
      state.scene = state.filteredSceneSteps[currentSceneIndex - 1];
    },
    showDetails(state) {
      state.scene = "details";
      document.querySelector("html").scrollTop = 0;
    },
    showPersonalize(state) {
      state.scene = "personalize";
      document.querySelector("html").scrollTop = 0;
    },
    showExpertMode(state) {
      state.isExpertMode = true;
      state.scene = "expert-mode";
      document.querySelector("html").scrollTop = 0;
    },
    showCheckoutLoading(state) {
      state.scene = "checkout-waiting";
      document.querySelector("html").scrollTop = 0;
    },
    nextLabelStep(state) {
      state.currentOrder.labelStep++;
    },
    setLabelStep(state, labelStep) {
      state.currentOrder.labelStep = labelStep;
    },
    setLabelFontSize(state, fontSize) {
      state.currentOrder.labelFontSize = fontSize;
    },
    prevLabelStep(state) {
      state.currentOrder.labelStep--;
    },
    isCalculated(state) {
      state.currentOrder.isCalculated = true;
    },
    isExpertMode(state) {
      return state.isExpertMode;
    },
    setValueInCurrentOrder(state, keyValue) {
      state.currentOrder[keyValue.key] = keyValue.value;
      this.commit("setScenesBasedOnOptions");
    },
    setValueInBranding(state, keyValue) {
      state.branding[keyValue.key] = keyValue.value;
    },
    startSubdomainConfigLoading(state) {
      state.subdomainConfigLoading = true;
    },
    finishSubdomainConfigLoading(state) {
      state.subdomainConfigLoading = false;
    },
    setSubdomainConfig(state, subdomainConfig) {
      state.subdomainConfig = subdomainConfig;
      updateRobotsMeta(
        shouldNoindexSubdomain(subdomainConfig) ? "noindex, nofollow" : null
      );

      for (let key of [
        "logo",
        "logoBlack",
        "logoLabelPNG",
        "showIndyVitLogo",
        "backButtonUrl",
        "disableCheckout",
        "subdomainQuiz",
        "subdomainExpertMode",
      ]) {
        if (
          subdomainConfig[key] !== undefined &&
          subdomainConfig[key] !== null &&
          subdomainConfig[key] !== ""
        ) {
          state.branding[key] = subdomainConfig[key];
        }
      }

      if (subdomainConfig.name) {
        state.branding.name = subdomainConfig.name;
      }

      for (let order of state.orders) {
        applySubdomainConfigToOrder(order, subdomainConfig);

        if (order.personalMix?.length > 0) {
          order.reCalculate(state.currency, {
            isBloodTestChecked: state.isBloodTestChecked,
            monthly: state.monthly,
            reorder: state.reorder,
          });
          applySubdomainConfigToOrder(order, subdomainConfig);
        }
      }
    },
    setFactorInCurrentOrder(state, keyValue) {
      state.currentOrder.factors[keyValue.key] = parseFloat(keyValue.value);
      // calculate factor if possible
      if (state.currentOrder.weight && state.currentOrder.height) {
        state.currentOrder.totalFactor = calc.totalFactor(
          state.currentOrder.factors,
          state.currentOrder.weight,
          state.currentOrder.height
        );
      }
    },
    setScenesBasedOnOptions(state) {
      // copy array and filter scenes based on choosen options
      state.filteredSceneSteps = [...state.sceneSteps];

      // if you are male we dont ask for pregnance
      if (state.currentOrder.gender === "male") {
        state.filteredSceneSteps = state.filteredSceneSteps.filter((scene) => {
          if (scene === "pregnant") {
            console.log("Remove pregnant from scenes because gender is male");
            return false;
          }
          return true;
        });
      }

      // remove additionalQuestion if one of these healthgoals is not set
      if (
        [
          "immune",
          "bones_joints",
          "digestion_gastrointestinal",
          "sleep",
          "stress",
          "mood",
          "skin_hair_nails",
          "energy",
          "fitness",
          "concentration",
          "heart",
          "fertility_male",
          "menopause",
          "potency",
        ].indexOf(state.currentOrder.healthPrio) == -1
      ) {
        state.filteredSceneSteps = state.filteredSceneSteps.filter((scene) => {
          if (scene === "additionalQuestion") {
            console.log(
              `Additional question removed because ${state.currentOrder.healthPrio}`
            );
            return false;
          }
          return true;
        });
      }

      // if you have intolerance to fish we dont ask for fishIntake
      for (let intolerance of state.currentOrder.intolerances) {
        if (intolerance.key == "unvertraeglichkeit_fisch") {
          state.filteredSceneSteps = state.filteredSceneSteps.filter(
            (scene) => {
              if (scene === "fishIntake") {
                console.log("Remove fishIntake because intolerance to fish");
                return false;
              }
              return true;
            }
          );
        }
      }

      // if you are vegan we dont ask for fishIntake
      if (state.currentOrder?.diet == "vegan") {
        state.filteredSceneSteps = state.filteredSceneSteps.filter((scene) => {
          if (scene === "fishIntake") {
            console.log("Remove fishIntake because intolerance vegan option");
            return false;
          }
          return true;
        });
      }

      // if your goals is not fitness we remove the exercise scene
      // if (state.currentOrder.healthGoals.length > 0 && state.scene != "healthGoals" && state.currentOrder.healthGoals.indexOf("fitness") == -1) {
      // 	state.filteredSceneSteps = state.filteredSceneSteps.filter(scene => {
      // 		if (scene === "exercise") {
      // 			return false;
      // 		}
      // 		return true;
      // 	})
      // }
    },
    saveProducts(state, products) {
      state.productsResponse = products;
      state.products = [];
      for (let product of products.resources) {
        let newProduct = new classes.Product(
          product,
          state.language,
          undefined,
          state.currency
        );
        // only add products if they are in stock
        if (!newProduct?.outOfStock) {
          state.products.push(newProduct);
        }
      }
    },
    saveUpselling(state, data) {
      state.productUpselling = [];
      for (let product of data.resources) {
        let upsellingProduct = new classes.Product(
          product,
          state.language,
          undefined,
          state.currency
        );
        upsellingProduct.isUpselling = true;
        // ignore custom mixes
        let ignoreIDs = ["7912980447479", "7912312766711"];
        if (ignoreIDs.indexOf(upsellingProduct.platform_id) >= 0) {
          return;
        }

        // only add upselling products if they are in stock
        if (!upsellingProduct?.outOfStock) {
          state.productUpselling.push(upsellingProduct);
        }
      }
    },
    filterUpsellingProducts(state) {
      // add static healthtopics
      let allHealthGoals = this.getters.healthGoals;
      if (this.getters.pregnant == "yes") {
        allHealthGoals.push("pregnant");
      }
      if (this.getters.allergic == "yes") {
        allHealthGoals.push("hayfever");
      }

      // filter all upselling products with the
      // currently selected healthGoals
      let results = [];
      for (let productUpselling of this.getters.productUpselling) {
        for (let selectedHealthTopic of allHealthGoals) {
          if (
            productUpselling?.healthTopics.find((topic) => {
              return topic == selectedHealthTopic;
            })
          ) {
            // check if product is not already in the list
            if (!results.find((result) => result.id == productUpselling.id)) {
              results.push(productUpselling);
            }
          }
        }
      }

      // KrillölID: "7661255065847"
      // LebertranID: "7661260046583"
      // LeinölID: "7661274136823"

      // NachtkerzenölID: "7610115784951"
      // SchwarzkümmelID: "7661292781815"
      if (this.getters.isVeggy) {
        results = results.filter(
          (prod) =>
            prod.platform_id !== "7661255065847" &&
            prod.platform_id !== "7661260046583"
        );
      } else {
        results = results.filter(
          (prod) => prod.platform_id !== "7661274136823"
        );
      }
      state.showUpsellings = results;

      if (state.isExpertMode && state.debugMode) {
        // DEBUG: Upselling debugging here
        console.log(
          "We are in expert mode. There are no upselling products here. So we enable all of them."
        );
        state.showUpsellings = this.getters.productUpselling;
      }
    },
    calculateProductResponse(state) {
      applySubdomainConfigToOrder(state.currentOrder, state.subdomainConfig);
      state.currentOrder.calculateQuiz(state);
      applySubdomainConfigToOrder(state.currentOrder, state.subdomainConfig);
    },
    reCalculateOrders(state) {
      for (let order of state.orders) {
        applySubdomainConfigToOrder(order, state.subdomainConfig);
        order.reCalculate(state.currency, {
          isBloodTestChecked: state.isBloodTestChecked,
          monthly: state.monthly,
          reorder: state.reorder,
        });
        applySubdomainConfigToOrder(order, state.subdomainConfig);
      }
      console.log("Recalculate all orders");
    },
    removeIng(state, ingObject) {
      // remove ing from personal mix
      for (let order of state.orders) {
        // find order to remove ing from
        if (order.id == ingObject.orderId) {
          for (let index = 0; index < order.personalMix.length; index++) {
            let ing = order.personalMix[index];
            if (ing.name == ingObject.name) {
              // found ing and remove it
              console.log("Ing found and removed", ing.name);
              order.personalMix.splice(index, 1);
            }
          }
          break;
        }
      }

      // remove order if no ing is left
      let selectedOrder = state.orders.find(
        (order) => order.id == ingObject.orderId
      );
      if (selectedOrder && selectedOrder.personalMix.length == 0) {
        console.log("No ing left in order, remove order");
        this.commit("removeOrder", ingObject.orderId);
      }

      // calculate
      this.commit("reCalculateOrders");

      // save new order status to hash
      this.commit("saveHash");
    },
    removeUpselling(state, rawProductIdWithIndex) {
      for (let index = 0; index < state.upsellingOrders.length; index++) {
        let upsellingOrder = state.upsellingOrders[index];
        if (
          upsellingOrder.raw_product_id + "_" + index ==
          rawProductIdWithIndex
        ) {
          console.log("Upselling product found, remove from order");
          state.upsellingOrders.splice(index, 1);
        }
      }
    },
    removeOrder(state, orderId) {
      // remove order from orderlist
      for (let index = 0; index < state.orders.length; index++) {
        let order = state.orders[index];
        if (order.id == orderId) {
          console.log("Remove order");
          state.orders.splice(index, 1);
        }
      }
    },
    addIngToPersonalMix(state, ing) {
      // check if we have an order or create a new one
      if (state.orders.length == 0) {
        console.log("There is no current order, create a new one");
        this.commit("newOrder");
      }

      // check if already in personalMix
      let found = false;
      for (let prod of state.currentOrder.personalMix) {
        if (prod.platform_id === ing.platform_id) {
          found = true;
        }
      }
      if (found) {
        console.log("Ing already exists in order", ing);
      } else {
        state.currentOrder.personalMix.push(ing);
      }
      this.commit("reCalculateOrders");
    },
    addUpsellingToOrder(state, raw_product_id) {
      // find product and add via raw_product_id
      let addThisProduct = state.productUpselling.find(
        (product) => product.raw_product_id == raw_product_id
      );
      if (addThisProduct) {
        state.upsellingOrders.push(addThisProduct);
      }
    },
    newOrder(state) {
      console.log("Create new order in orders list and change current order");
      state.currentOrder.isVisible = false;
      let newOrder = new classes.Order(state.orders.length + 1);
      applySubdomainConfigToOrder(newOrder, state.subdomainConfig);
      state.currentOrder = newOrder;
      state.orders[state.currentOrder.id - 1] = newOrder;
    },
    readHash(state) {
      let hash = document.location.hash;
      if (hash?.indexOf("cart=") >= 0) {
        let hashArray = hash.split("cart=");
        let hashOrderList = JSON.parse(decodeURIComponent(hashArray[1]));

        if (hashOrderList.length > 0) {
          console.log("Read order from url hash");
          for (let i = 0; i < hashOrderList.length; i++) {
            let savedValue = hashOrderList[i];
            let order = new classes.Order(i + 1);

            // set optional params if saved in url
            if (savedValue.name) {
              order.name = savedValue.name;
            }
            if (savedValue.originalName) {
              order.originalName = savedValue.originalName;
            }
            // label was removed from pdf
            if (savedValue.label) {
              order.label = savedValue.label;
            }
            if (savedValue.labelColor) {
              order.labelColor = savedValue.labelColor;
            }
            if (savedValue.labelPattern) {
              order.labelPattern = savedValue.labelPattern;
            }
            if (savedValue.capsuleColor) {
              order.capsuleColor = savedValue.capsuleColor;
              let found = helper
                .getLabelColors()
                .filter((obj) => obj.key == order.labelColor);
              if (found.length > 0) {
                order.labelColorHexTop = found[0].color_top;
                order.labelColorHexBottom = found[0].color_bottom;
              }
            }
            if (savedValue.capsulaMaterial) {
              order.capsulaMaterial = savedValue.capsulaMaterial;
            }
            if (savedValue.email) {
              order.email = savedValue.email;
            }
            if (savedValue.totalFactor) {
              order.totalFactor = savedValue.totalFactor;
            }
            if (savedValue.fragrance) {
              order.fragrance = savedValue.fragrance;
            }
            if (savedValue.diet) {
              order.diet = savedValue.diet;
            }

            for (let savedProd of savedValue.personalMix) {
              // search with id and amount for full product information
              let fullProduct = {};

              // INFO: We have switched from id to platform_id in the product list
              const relevantProductId =
                getProductIdByLegacyId(savedProd.id) || savedProd.id;

              for (let prod of state.products) {
                // find product id in product api results
                if (String(prod.platform_id) === String(relevantProductId)) {
                  Object.assign(fullProduct, prod);
                  fullProduct.calculate = prod.calculate;
                  fullProduct.quizResult = savedProd.amount;
                  fullProduct.amount = savedProd.amount;
                }
              }
              if (!Object.keys(fullProduct).length) {
                console.log("Product not found in product list", savedProd);
                continue;
              }
              order.personalMix.push(fullProduct);
            }

            order.reCalculate(state.currency);

            // keep track of orders
            state.currentOrder = order;
            state.orders[i] = order;

            state.hashRead = true;
          }
        } else {
          this.commit("saveHash");
        }
        // important calculation for totalcapsulas value
        this.commit("reCalculateOrders");
      }
    },
    createHash(state) {
      let saveOrders = [];
      for (let order of state.orders) {
        let item = {};
        if (order.name) {
          item.name = order.name;
        }
        if (order.originalName) {
          item.originalName = order.originalName;
        }
        // label was removed from pdf
        if (order.label) {
          item.label = order.label;
        }
        if (order.email) {
          item.email = order.email;
        }
        if (order.labelColor) {
          item.labelColor = order.labelColor;
        }
        if (order.labelPattern) {
          item.labelPattern = order.labelPattern;
        }
        if (order.capsuleColor) {
          item.capsuleColor = order.capsuleColor;
        }
        if (order.capsuleColor) {
          item.fragrance = order.fragrance;
        }
        if (order.diet) {
          item.diet = order.diet;
        }
        if (order.capsulaMaterial) {
          item.capsulaMaterial = order.capsulaMaterial;
        }
        if (order.totalFactor) {
          item.totalFactor = order.totalFactor;
        }
        item.personalMix = order.personalMix.map((prod) => {
          return { id: prod.platform_id, amount: prod.amount };
        });
        saveOrders.push(item);
      }
      state.hash = "#cart=" + JSON.stringify(saveOrders);
    },
    saveHash(state) {
      let saveOrders = [];
      for (let order of state.orders) {
        let item = {};
        if (order.name) {
          item.name = order.name;
        }
        if (order.originalName) {
          item.originalName = order.originalName;
        }
        // label was removed from pdf
        if (order.label) {
          item.label = order.label;
        }
        if (order.email) {
          item.email = order.email;
        }
        if (order.labelColor) {
          item.labelColor = order.labelColor;
        }
        if (order.capsuleColor) {
          item.capsuleColor = order.capsuleColor;
        }
        if (order.capsuleColor) {
          item.fragrance = order.fragrance;
        }
        if (order.diet) {
          item.diet = order.diet;
        }
        if (order.capsulaMaterial) {
          item.capsulaMaterial = order.capsulaMaterial;
        }
        if (order.totalFactor) {
          item.totalFactor = order.totalFactor;
        }
        item.personalMix = order.personalMix.map((prod) => {
          return { id: prod.platform_id, amount: prod.amount };
        });
        saveOrders.push(item);
      }
      document.location.hash = "#cart=" + JSON.stringify(saveOrders);
      console.log("Save orders in url hash");
    },
    readSavedHash(state) {
      let hash = window.location.hash;
      if (
        hash.indexOf("cart=") >= 0 &&
        state.currentOrder.personalMix.length == 0
      ) {
        console.log("Read order from url hash");
        this.commit("readHash");
      }
    },
  },
});

export default store;
